import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RequiredLabel } from "../../../components/form/RequiredLabel";
import { Input } from "../../../components/input";
import { ImageUploadCropper } from "../../../components/image-upload-cropper";
import { SelectV2 } from "../../../components/select-v2";

export type CategoryEditorInitialValues = {
  name: string;
  type: string;
  imageUrl: string | null;
};

export type CategoryEditorSubmitValues = {
  name: string;
  type: string;
  image: File | null;
};

type CategoryTypeOption = {
  value: string;
  label: string;
};

type CategoryEditorProps = {
  formId: string;
  isOpen: boolean;
  initialValues: CategoryEditorInitialValues;
  typeOptions: CategoryTypeOption[];
  isEditMode: boolean;
  onSubmit: (values: CategoryEditorSubmitValues) => void | Promise<void>;
};

export function CategoryEditor({
  formId,
  isOpen,
  initialValues,
  typeOptions,
  isEditMode,
  onSubmit,
}: CategoryEditorProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialValues.name);
  const [type, setType] = useState(initialValues.type);
  const [image, setImage] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setName(initialValues.name);
    setType(initialValues.type);
    setImage(null);
    setImageError(undefined);
  }, [initialValues.imageUrl, initialValues.name, initialValues.type, isOpen]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isEditMode && !image) {
      setImageError(t("categories.image_required"));
      return;
    }

    void onSubmit({ name: name.trim(), type: type.trim(), image });
  };

  return (
    <form
      id={formId}
      onSubmit={handleSubmit}
      className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2"
    >
      <div className="md:col-span-2">
        <ImageUploadCropper
          value={image ?? initialValues.imageUrl}
          onChange={(file) => {
            setImage(file);
            setImageError(undefined);
          }}
          aspect={1}
          title={t("categories.image")}
          description={t("categories.image_crop_hint")}
          outputFileName="category_icon.jpg"
          error={imageError}
        />
      </div>
      <label className="flex flex-col gap-1 text-sm font-medium">
        <RequiredLabel required>{t("categories.name")}</RequiredLabel>
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          maxLength={100}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm font-medium">
        <RequiredLabel required>{t("categories.type")}</RequiredLabel>
        <SelectV2
          value={type}
          onChange={setType}
          options={typeOptions}
          placeholder={t("categories.select_type")}
          ariaLabel={t("categories.type")}
        />
      </label>
    </form>
  );
}
